"use client";

import { Text, SectionCard, CardTitle, CardDescription, Badge, Section } from "@/src/components";

export default function DocsBlocksFilterBuilder() {
  return (
    <>
      {/* Header */}
      <Section>
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <Text as="h1" className="text-h2 font-bold">
              Filter Builder
            </Text>
            <Badge variant="secondary">Block</Badge>
          </div>
          <Text as="p" className="text-lg text-muted-foreground max-w-2xl">
            A dynamic filter builder for creating complex query conditions.
            Supports multiple filter groups with AND/OR logic.
          </Text>
        </div>
      </Section>

      {/* Features */}
      <Section>
        <SectionCard
        header={<CardTitle>Features</CardTitle>}
        contentClassName="p-6"
      >
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          <div className="flex items-center gap-2">
            <Badge variant="secondary">-</Badge>
            <Text as="p" className="text-sm">Multiple filter groups</Text>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="secondary">-</Badge>
            <Text as="p" className="text-sm">AND/OR logic</Text>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="secondary">-</Badge>
            <Text as="p" className="text-sm">Custom field types</Text>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="secondary">-</Badge>
            <Text as="p" className="text-sm">Comparison operators</Text>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="secondary">-</Badge>
            <Text as="p" className="text-sm">Add/remove conditions</Text>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="secondary">-</Badge>
            <Text as="p" className="text-sm">Nested groups</Text>
          </div>
        </div>
      </SectionCard>
      </Section>

      {/* Basic Usage */}
      <Section>
        <SectionCard
        header={
          <>
            <CardTitle>Basic Usage</CardTitle>
            <CardDescription>Define fields and handle filter changes</CardDescription>
          </>
        }
        contentClassName="p-6"
      >
        <div className="bg-muted/50 rounded-lg p-4 font-mono text-sm overflow-x-auto">
          <pre>{`import { FilterBuilder } from "@/registry/new-york/blocks/filter-builder/filter-builder";

const fields = [
  { name: "name", label: "Name", type: "text" },
  { name: "email", label: "Email", type: "text" },
  { name: "status", label: "Status", type: "select", options: [
    { label: "Active", value: "active" },
    { label: "Inactive", value: "inactive" },
  ]},
  { name: "createdAt", label: "Created At", type: "date" },
  { name: "amount", label: "Amount", type: "number" },
];

<FilterBuilder
  fields={fields}
  value={filters}
  onChange={setFilters}
/>`}</pre>
        </div>
      </SectionCard>
      </Section>

      {/* Field Types */}
      <Section>
        <SectionCard
        header={
          <>
            <CardTitle>Field Types</CardTitle>
            <CardDescription>Supported field types and their operators</CardDescription>
          </>
        }
        contentClassName="p-6"
      >
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b">
              <th className="text-left py-2 font-semibold">Type</th>
              <th className="text-left py-2 font-semibold">Operators</th>
              <th className="text-left py-2 font-semibold">Input</th>
            </tr>
          </thead>
          <tbody>
            <tr className="border-b">
              <td className="py-2 font-mono text-xs">text</td>
              <td className="py-2 text-muted-foreground">equals, contains, starts with, ends with</td>
              <td className="py-2 text-muted-foreground">Text input</td>
            </tr>
            <tr className="border-b">
              <td className="py-2 font-mono text-xs">number</td>
              <td className="py-2 text-muted-foreground">equals, greater than, less than, between</td>
              <td className="py-2 text-muted-foreground">Number input</td>
            </tr>
            <tr className="border-b">
              <td className="py-2 font-mono text-xs">date</td>
              <td className="py-2 text-muted-foreground">equals, before, after, between</td>
              <td className="py-2 text-muted-foreground">Date picker</td>
            </tr>
            <tr className="border-b">
              <td className="py-2 font-mono text-xs">select</td>
              <td className="py-2 text-muted-foreground">equals, not equals, in</td>
              <td className="py-2 text-muted-foreground">Select dropdown</td>
            </tr>
            <tr className="border-b">
              <td className="py-2 font-mono text-xs">boolean</td>
              <td className="py-2 text-muted-foreground">is true, is false</td>
              <td className="py-2 text-muted-foreground">Checkbox</td>
            </tr>
          </tbody>
        </table>
      </SectionCard>
      </Section>

      {/* Import */}
      <Section>
        <SectionCard
          header={<CardTitle>Import</CardTitle>}
          contentClassName="p-6"
        >
          <div className="bg-muted/50 rounded-lg p-4 font-mono text-sm">
            <code>{`import { FilterBuilder } from "@/registry/new-york/blocks/filter-builder/filter-builder";`}</code>
          </div>
        </SectionCard>
      </Section>

      {/* Props */}
      <Section>
        <SectionCard
        header={<CardTitle>Props</CardTitle>}
        contentClassName="p-6"
      >
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b">
              <th className="text-left py-2 font-semibold">Prop</th>
              <th className="text-left py-2 font-semibold">Type</th>
              <th className="text-left py-2 font-semibold">Description</th>
            </tr>
          </thead>
          <tbody>
            <tr className="border-b">
              <td className="py-2 font-mono text-xs">fields</td>
              <td className="py-2 font-mono text-xs text-muted-foreground">FilterField[]</td>
              <td className="py-2 text-muted-foreground">Available fields to filter on</td>
            </tr>
            <tr className="border-b">
              <td className="py-2 font-mono text-xs">value</td>
              <td className="py-2 font-mono text-xs text-muted-foreground">FilterGroup</td>
              <td className="py-2 text-muted-foreground">Current filter state</td>
            </tr>
            <tr className="border-b">
              <td className="py-2 font-mono text-xs">onChange</td>
              <td className="py-2 font-mono text-xs text-muted-foreground">(filters: FilterGroup) =&gt; void</td>
              <td className="py-2 text-muted-foreground">Called when filters change</td>
            </tr>
            <tr className="border-b">
              <td className="py-2 font-mono text-xs">maxDepth</td>
              <td className="py-2 font-mono text-xs text-muted-foreground">number</td>
              <td className="py-2 text-muted-foreground">Maximum nesting depth</td>
            </tr>
          </tbody>
        </table>
      </SectionCard>
      </Section>
    </>
  );
}
